import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../lib/brand";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@marianne.law");
  const [password, setPassword] = useState("Demo@2026");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to Marianne.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAF8]" data-testid="login-page">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#2A0F5A] text-[#FAFAF8]">
        <Link to="/" className="flex items-center gap-2.5" data-testid="auth-brand">
          <Logo size={30} />
          <span className="font-serif text-2xl leading-none">Marianne</span>
        </Link>
        <div>
          <div className="label-eyebrow text-[#C5A059] mb-3">Welcome back</div>
          <h1 className="font-serif text-5xl tracking-tight leading-tight">
            The scales<br /> remember you.
          </h1>
          <p className="mt-6 text-[#FAFAF8]/70 max-w-md">
            Your cases, drafts and evidence await. Sign in to continue building your record.
          </p>
        </div>
        <div className="text-xs text-[#FAFAF8]/50">© 2026 Mahakali Tribunal</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#4A5568] mb-10 hover:text-[#2A0F5A]" data-testid="back-home-link">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="label-eyebrow text-[#C5A059] mb-3">Sign in</div>
          <h2 className="font-serif text-4xl font-medium mb-8">Enter the tribunal.</h2>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="label-eyebrow mb-2 block">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-[#E8E3F0] focus-visible:ring-0 focus-visible:border-[#2A0F5A] bg-white"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 block">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none border-[#E8E3F0] focus-visible:ring-0 focus-visible:border-[#2A0F5A] bg-white"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-[#4A5568]">
            New to Marianne?{" "}
            <Link to="/register" className="text-[#2A0F5A] underline underline-offset-4 hover:text-[#C5A059]" data-testid="link-register">
              Create an account
            </Link>
          </div>

          <div className="mt-10 p-4 border border-[#E8E3F0] bg-white">
            <div className="label-eyebrow mb-1.5">Try the demo</div>
            <div className="font-mono text-xs text-[#4A5568]">demo@marianne.law / Demo@2026</div>
          </div>
        </div>
      </div>
    </div>
  );
}
